import React from "react";
import * as Icon from "react-feather";

import WComponent from "../common/WComponent";
import ToolsModal from "../clients/Tools/kit/ToolsModal";
import ToolsCodeBlock from "../clients/Tools/kit/ToolsCodeBlock";
import ToolsReviewItem from "../clients/Tools/kit/ToolsReviewItem";
import PlaybookScriptService from "../../services/PlaybookScriptService";
import PlaybookScriptEditModal from "./PlaybookScriptEditModal";
import PlaybookScriptTestForm from "./PlaybookScriptTestForm";
import PlaybookScriptResults from "./PlaybookScriptResults";

import "./playbook-build-scripts.css";

const DETAIL_MODAL_WIDTH = 860;
const BUSY_KEYS = {
	redraft: "script-redraft",
	review: "script-review",
	compare: "script-compare",
	approve: "script-approve",
	addToLibrary: "script-add-to-library",
};
const VERDICTS = {
	agreed: "agreed",
	disagreed: "disagreed",
};
const VERDICT_TEXTS = {
	agreed: "Agreed",
	disagreed: "Disagreed",
};
const PASSED = "passed";
const ADDED_TO_LIBRARY_TEXT = "Added to the firm's scripts.";

class PlaybookScriptDetailModal extends WComponent {
	constructor(props) {
		super(props);
		this.state = {
			isEditOpen: false,
			testAnswer: null,
			compareAnswer: null,
			reviewRefusal: null,
			approveAnswer: null,
			addedAnswer: null,
			refusal: null,
		};
	}

	onTest = async ({ answer }) => {
		const { script } = this.props;
		await this.update({ testAnswer: { pinId: script.pinId, revisionNumber: script.revisionNumber, answer } });
	};

	onReview = async () => {
		const { script, sessionId, runScriptAction } = this.props;
		const askForReview = () => PlaybookScriptService.reviewBuildScript({ sessionId, pinId: script.pinId });
		const { refusal } = await runScriptAction({ busyKey: BUSY_KEYS.review, action: askForReview, pinId: script.pinId });
		const reviewRefusal = refusal ? { pinId: script.pinId, revisionNumber: script.revisionNumber, sentence: refusal } : null;
		await this.update({ reviewRefusal, refusal: null });
	};

	onVerdict = async ({ findingId, verdict }) => {
		const markFinding = () => PlaybookScriptService.markBuildScriptFinding({ sessionId: this.props.sessionId, findingId, verdict });
		const { refusal } = await this.props.runScriptAction({ busyKey: `script-verdict-${findingId}-${verdict}`, action: markFinding, pinId: this.props.script.pinId });
		await this.update({ refusal });
	};

	onCompare = async () => {
		const { script, sessionId, runScriptAction } = this.props;
		const compare = () => PlaybookScriptService.compareBuildScript({ sessionId, pinId: script.pinId });
		const { answer, refusal } = await runScriptAction({ busyKey: BUSY_KEYS.compare, action: compare, pinId: script.pinId });
		const compareAnswer = answer ? { pinId: script.pinId, revisionNumber: script.revisionNumber, answer } : null;
		await this.update({ refusal, compareAnswer });
	};

	onApprove = async () => {
		const { script, sessionId, runScriptAction } = this.props;
		const confirmedDifferencesFingerprint = script.regression ? script.regression.differencesFingerprint : null;
		const approve = () => PlaybookScriptService.approveBuildScript({ sessionId, pinId: script.pinId, note: null, confirmedDifferencesFingerprint });
		const { answer, refusal } = await runScriptAction({ busyKey: BUSY_KEYS.approve, action: approve, pinId: script.pinId });
		const approveAnswer = answer ? { pinId: script.pinId, revisionNumber: script.revisionNumber, nextStep: answer.nextStep } : null;
		await this.update({ refusal, approveAnswer });
	};

	onRedraft = async () => {
		const { script, sessionId, runScriptAction } = this.props;
		const writeAgain = () => PlaybookScriptService.writeBuildScript({ sessionId, gapId: script.gapId, scriptKey: script.scriptKey });
		const { refusal } = await runScriptAction({ busyKey: BUSY_KEYS.redraft, action: writeAgain, pinId: script.pinId });
		await this.update({ refusal });
	};

	onAddToLibrary = async () => {
		const addToLibrary = () => PlaybookScriptService.addScriptToLibrary({ sessionId: this.props.sessionId, pinId: this.props.script.pinId });
		const { script } = this.props;
		const { answer, refusal } = await this.props.runScriptAction({ busyKey: BUSY_KEYS.addToLibrary, action: addToLibrary, pinId: script.pinId });
		const addedAnswer = answer ? { pinId: script.pinId, revisionNumber: script.revisionNumber } : null;
		await this.update({ refusal, addedAnswer });
	};

	render() {
		const { script, viewer, sessionId, isOpen, isConversationOpen, busyKey, onClose, runScriptAction } = this.props;
		const { isEditOpen, testAnswer, compareAnswer, reviewRefusal, approveAnswer, addedAnswer, refusal } = this.state;
		if (!script) return null;

		const isOfThisRevision = held => Boolean(held) && held.pinId === script.pinId && held.revisionNumber === script.revisionNumber;
		const heldComparison = isOfThisRevision(compareAnswer) ? compareAnswer.answer : null;
		const isHeldComparisonUnlisted = Boolean(heldComparison) && heldComparison.outcome !== PASSED;
		const regression = isHeldComparisonUnlisted ? { ...(script.regression || {}), ...heldComparison } : script.regression;
		const scriptAsShown = { ...script, regression };
		const reviewRefusalSentence = isOfThisRevision(reviewRefusal) ? reviewRefusal.sentence : null;
		const card = PlaybookScriptService.describeScriptCard({ script: scriptAsShown, viewer, reviewRefusal: reviewRefusalSentence, isConversationOpen });
		const isBusy = busyKey !== null && busyKey !== undefined;
		const isLive = card.isOnItsGap && viewer.areScriptsOn && isConversationOpen;
		const contextNote = card.notOnItsStepText || card.closedText;

		const freshTestAnswer = isOfThisRevision(testAnswer) ? testAnswer.answer : null;
		const freshResults = freshTestAnswer ? freshTestAnswer.results : null;
		const hasPassedTest = Boolean(script.newestTest) && script.newestTest.outcome === PASSED;
		const isTestAgainShown = !freshResults && hasPassedTest && !card.isEmptyDataPass;

		const isReviewAskable = isLive && card.canAskForReview;
		const hasFindings = card.findings.length > 0;

		const hasShownDifferences = Boolean(regression) && Boolean(regression.shownDifferences) && regression.shownDifferences.length > 0;
		const isCompareShown = Boolean(card.compareText) || card.canCompare || hasShownDifferences;

		const nextStep = isOfThisRevision(approveAnswer) && script.isApproved ? approveAnswer.nextStep : null;
		const isBlockedFromApproval = !script.isApproved && card.isOnItsGap && isConversationOpen;
		const blockers = isBlockedFromApproval ? card.approveBlockers : [];
		const hasBlockers = blockers.length > 0;
		const isAddedToLibrary = isOfThisRevision(addedAnswer);
		const isAddToLibraryShown = card.canAddToLibrary && !isAddedToLibrary;
		const isEmptyDataPassNoted = card.isEmptyDataPass && card.canApprove;
		const hasFooterActions = card.canApprove || isAddToLibraryShown;
		const hasFooter = hasBlockers || Boolean(refusal) || hasFooterActions || Boolean(card.approvedText);

		const context = (
			<div className="playbook-script-detail__context">
				{card.plainDescription && <p className="playbook-script-detail__description">{card.plainDescription}</p>}
				<span className="playbook-script-detail__meta">
					{card.kindText} · {card.revisionText} · {card.sourceText}
				</span>
				{contextNote && <span className="playbook-script-detail__meta playbook-script-detail__context-note">{contextNote}</span>}
			</div>
		);

		const footer = hasFooter ? (
			<div className="playbook-script-detail__footer">
				{hasBlockers && (
					<div className="tk-notice playbook-script-detail__blockers">
						{blockers.map(blocker => (
							<p key={blocker} className="playbook-script-detail__blocker">
								{blocker}
							</p>
						))}
					</div>
				)}
				{refusal && (
					<div className="tk-notice tk-notice--danger">
						<div className="tk-notice__title">
							<Icon.AlertTriangle size={14} />
							{refusal}
						</div>
					</div>
				)}
				<div className="playbook-script-detail__footer-row">
					<span className="playbook-script-detail__status">
						{card.approvedText && (
							<span className="playbook-script-detail__approved">
								<Icon.CheckCircle size={14} />
								{card.approvedText}
							</span>
						)}
						{nextStep && <span className="playbook-script-detail__next-step">{nextStep}</span>}
						{isAddedToLibrary && (
							<span className="playbook-script-detail__next-step playbook-script-detail__added">
								<Icon.BookOpen size={13} />
								{ADDED_TO_LIBRARY_TEXT}
							</span>
						)}
						{isEmptyDataPassNoted && <span className="playbook-script-detail__next-step">{card.testText}</span>}
					</span>
					{hasFooterActions && (
						<div className="tk-modal-actions">
							{isAddToLibraryShown && (
								<button type="button" className="tk-button tk-button--secondary" onClick={this.onAddToLibrary} disabled={isBusy}>
									<Icon.BookOpen size={14} />
									{busyKey === BUSY_KEYS.addToLibrary ? "Adding…" : "Add to the firm's scripts"}
								</button>
							)}
							{card.canApprove && (
								<button type="button" className="tk-button tk-button--primary" onClick={this.onApprove} disabled={isBusy}>
									<Icon.Check size={14} />
									{busyKey === BUSY_KEYS.approve ? "Approving…" : "Approve"}
								</button>
							)}
						</div>
					)}
				</div>
			</div>
		) : null;

		return (
			<>
				<ToolsModal open={isOpen} onClose={onClose} title={card.title} width={DETAIL_MODAL_WIDTH} context={context} footer={footer}>
					<div className="playbook-script-detail">
						<section className="playbook-script-detail__section">
							<div className="playbook-script-detail__section-head">
								<span className="playbook-script-detail__section-title">Code</span>
								{isLive && (
									<span className="playbook-script-detail__actions">
										<button type="button" className="tk-text-action" onClick={() => this.update({ isEditOpen: true })} disabled={isBusy}>
											<Icon.Edit3 size={13} />
											Edit
										</button>
										{card.canRedraft && (
											<button type="button" className="tk-text-action" onClick={this.onRedraft} disabled={isBusy}>
												<Icon.RefreshCw size={13} />
												{busyKey === BUSY_KEYS.redraft ? "Writing…" : "Ask Workflow to write it again"}
											</button>
										)}
									</span>
								)}
							</div>
							{card.attemptsText && <span className="playbook-script-detail__line playbook-script-detail__line--muted">{card.attemptsText}</span>}
							<ToolsCodeBlock code={script.code} />
						</section>

						<section className="playbook-script-detail__section">
							<div className="playbook-script-detail__section-head">
								<span className="playbook-script-detail__section-title">Test</span>
							</div>
							{!freshResults && (
								<span className="playbook-script-detail__line">
									{card.testText}
									{isTestAgainShown && <span className="playbook-script-detail__hint"> Test again to see the rows.</span>}
								</span>
							)}
							{!freshResults && card.actionsText && <span className="playbook-script-detail__line">{card.actionsText}</span>}
							{isLive && <PlaybookScriptTestForm script={script} sessionId={sessionId} busyKey={busyKey} runScriptAction={runScriptAction} onTested={this.onTest} />}
							{freshResults && <PlaybookScriptResults results={freshResults} manifest={script.manifest} />}
						</section>

						<section className="playbook-script-detail__section">
							<div className="playbook-script-detail__section-head">
								<span className="playbook-script-detail__section-title">Review</span>
								{isReviewAskable && (
									<button type="button" className="tk-button tk-button--secondary tk-button--compact" onClick={this.onReview} disabled={isBusy}>
										<Icon.Search size={13} />
										{busyKey === BUSY_KEYS.review ? "Reviewing…" : "Have it reviewed"}
									</button>
								)}
							</div>
							<span className="playbook-script-detail__line">{card.reviewText}</span>
							{hasFindings && (
								<div className="playbook-script-detail__findings">
									{card.findings.map(finding => (
										<ToolsReviewItem
											key={finding.findingId}
											status={finding.verdict === VERDICTS.disagreed ? "na" : "warn"}
											label={finding.label}
											detail={finding.detail}
											after={
												finding.canMark ? (
													<span className="playbook-script-detail__verdict-actions">
														<button
															type="button"
															className={
																finding.verdict === VERDICTS.agreed
																	? "tk-button tk-button--secondary tk-button--compact playbook-script-detail__verdict-button--chosen"
																	: "tk-button tk-button--secondary tk-button--compact"
															}
															onClick={() => this.onVerdict({ findingId: finding.findingId, verdict: VERDICTS.agreed })}
															disabled={isBusy}
															aria-pressed={finding.verdict === VERDICTS.agreed}
														>
															{busyKey === `script-verdict-${finding.findingId}-${VERDICTS.agreed}` ? "Saving…" : "Agree"}
														</button>
														<button
															type="button"
															className={
																finding.verdict === VERDICTS.disagreed
																	? "tk-button tk-button--secondary tk-button--compact playbook-script-detail__verdict-button--chosen"
																	: "tk-button tk-button--secondary tk-button--compact"
															}
															onClick={() => this.onVerdict({ findingId: finding.findingId, verdict: VERDICTS.disagreed })}
															disabled={isBusy}
															aria-pressed={finding.verdict === VERDICTS.disagreed}
														>
															{busyKey === `script-verdict-${finding.findingId}-${VERDICTS.disagreed}` ? "Saving…" : "Disagree"}
														</button>
													</span>
												) : finding.verdict ? (
													<span className={`tk-badge playbook-script-detail__verdict playbook-script-detail__verdict--${finding.verdict}`}>{VERDICT_TEXTS[finding.verdict]}</span>
												) : null
											}
										>
											{finding.evidence && <p className="playbook-script-detail__evidence">{finding.evidence}</p>}
										</ToolsReviewItem>
									))}
								</div>
							)}
						</section>

						{isCompareShown && (
							<section className="playbook-script-detail__section">
								<div className="playbook-script-detail__section-head">
									<span className="playbook-script-detail__section-title">Compare</span>
									{card.canCompare && (
										<button type="button" className="tk-button tk-button--secondary tk-button--compact" onClick={this.onCompare} disabled={isBusy}>
											<Icon.GitPullRequest size={13} />
											{busyKey === BUSY_KEYS.compare ? "Comparing…" : "Compare with its stored test"}
										</button>
									)}
								</div>
								{card.compareText && <span className="playbook-script-detail__line">{card.compareText}</span>}
								{hasShownDifferences && <PlaybookScriptResults regression={regression} manifest={script.manifest} />}
							</section>
						)}
					</div>
				</ToolsModal>
				{isOpen && isEditOpen && (
					<PlaybookScriptEditModal
						key={script.revisionId}
						script={script}
						sessionId={sessionId}
						isOpen
						busyKey={busyKey}
						onClose={() => this.update({ isEditOpen: false })}
						runScriptAction={runScriptAction}
					/>
				)}
			</>
		);
	}
}

export default PlaybookScriptDetailModal;
